import { ItemMetadata } from 'src/API';
import type { LambdaResultShape } from 'src/utils/lambdaResultUtils';

export enum Attribute_Type {
  STANDARD = 'STANDARD',
  PRODUCT_LINE = 'PRODUCT LINE',
  PROGRAM = 'PROGRAM',
}

export enum ChangeReviewStatus {
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  PENDING = 'PENDING',
}

export enum AdminActionStatus {
  PENDING_APPROVAL = 'Pending',
  NO_ACTION = 'No Action',
}

// Response of listAttributeDetails Query
export type AttributeDetailsResponse = {
  data: Array<AttributeDetails>;
  pagination: PaginationMetadata;
};

export type PaginationMetadata = {
  current_page: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
  total_count: number;
  total_pages: number;
};

export interface AttributeDetails {
  attribute_type_id: number;

  attribute_type_label: string; // Example: CATEGORY | CHANNEL | INDOOR/OUTDOOR | PC/PK COUNT
  attribute_level_label: Attribute_Type; // STANDARD | PROGRAM | PRODUCT LINE |

  attribute_type_justification?: string | null;
  attribute_type_status: ChangeReviewStatus;

  attribute_value_details: Array<AttributeValueDetails>;
  item_metadata: ItemMetadata;
}

export interface AttributeValueDetails {
  attribute_value_id: number;
  attribute_value_label: string;

  attribute_value_justification?: string | null;
  attribute_value_status: ChangeReviewStatus;

  product_line_count: number;
  rpn_count: number;
  fpn_count: number;
  item_metadata: ItemMetadata;
}

// ------------------------------------------------------------

export interface ManageAttributesTableItems extends AttributeDetails {
  admin_action_status: AdminActionStatus;
  attribute_value_labels: string;
  rpn_count: number;
  fpn_count: number;
  product_line_count: number;
}

export type AttributeDetailsInput = {
  attribute_level_label: string;
  attribute_type_id?: number | null;
  attribute_type_justification?: string | null;
  attribute_type_label: string;
  attribute_type_status: ChangeReviewStatus;
  attribute_value_details: Array<AttributeValueDetailsInput>;
  item_metadata: ItemMetadata;
};

export type AttributeValueDetailsInput = {
  attribute_value_id?: number | null;
  attribute_value_justification?: string | null;
  attribute_value_label: string;
  attribute_value_status: ChangeReviewStatus;
  item_metadata: ItemMetadata;
};

export interface AttributeState {
  masterAttributesData: AttributeDetails[];
  selectedAttribute: ManageAttributesTableItems | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

export interface ExistingAttributesTableItems {
  attributeLevelLabel: string;
  attributeTypeId: number;
  attributeTypeLabel: string;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata: ItemMetadata;
  attributeValueId: number;
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string | null;
  rpn_count: number;
  fpn_count: number;
  productLines: number[];
  fpnCount: number;
  rpnCount: number;
  productLineCount: number;
  attributeValueItemMetadata?: ItemMetadata;
  requestedBy: string;
  requestedAt: string;
  reviewedBy: string;
  reviewedAt: string;
}

export interface PendingAttributesTableItems {
  attributeLevelLabel: string;
  attributeTypeId: number;
  attributeTypeLabel: string;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata: ItemMetadata;
  attributeValueId: number;
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string | null;
  attributeValueItemMetadata: ItemMetadata;
  requestedBy: string;
  requestedAt: string;
}

export interface AttributeValuesForm {
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string;
}

export interface CreateAttributeFormValues {
  attributeLevelLabel: string | null;
  attributeTypeId?: number | null;
  attributeTypeLabel: string | null;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata?: ItemMetadata | null;
  attributeValues: AttributeValuesForm[];
}

export interface CreateAttributeFormState {
  values: CreateAttributeFormValues;
  dirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  isValidating: boolean;
}

// Ticketing types for attribute approval ticket creation
export interface CreateAttributeApprovalTicketInput {
  attribute_type: string;
  attribute_values: string[];
  attribute_level: string;
  user_alias: string;
  justifications: string[];
}

export interface TicketCreationResponse extends LambdaResultShape {
  ticket_id: string;
}

// ============================================================================
// AI-assisted attribute/value matching (Option A: client-side)
// ============================================================================

/**
 * How one requested value maps onto a specific candidate attribute: either it
 * already exists there (matched, so it would be reused) or it does not (missing,
 * so it would be created new under that attribute).
 */
export interface ValueCoverage {
  /** The value label the user typed (e.g. "SUPPLIER"). */
  requestedLabel: string;
  /**
   * The existing value label this maps to under the candidate attribute, when
   * matched (e.g. "VENDOR"). Undefined when this value is missing there.
   */
  existingLabel?: string;
  /** Match confidence in [0, 1] for a matched value; 0 when missing. */
  score: number;
  /** True when the value already exists under the candidate attribute. */
  matched: boolean;
}

/**
 * A single existing attribute proposed as the ONE home for all of the user's
 * requested values (consolidation model). Because the product consumes a single
 * attribute, the user picks exactly one of these; matched values are reused and
 * missing values are created under it.
 */
export interface CandidateAttribute {
  /** The existing attribute being proposed as the home. */
  attribute: AttributeDetails;
  /** Per-value breakdown of what is matched vs missing under this attribute. */
  coverage: ValueCoverage[];
  /** Requested values that already exist here (reused). */
  matchedValues: ValueCoverage[];
  /** Requested values that do NOT exist here (created new). */
  missingValues: ValueCoverage[];
  /** matchedValues.length / total requested values, in [0, 1]. */
  coverageRatio: number;
  /** Average match score across matched values (for ranking ties). */
  averageScore: number;
}

/**
 * Aggregate outcome for an entire form submission under the consolidation
 * model. Ranks existing attributes by how well they can host ALL of the user's
 * requested values, so the user can pick a single home.
 */
export interface AttributeMatchAssessment {
  /** Every requested value label the user typed (order preserved). */
  requestedValueLabels: string[];
  /**
   * Candidate homes that cover at least the minimum ratio of requested values,
   * ranked best-first (by coverage, then average score).
   */
  candidateAttributes: CandidateAttribute[];
  /**
   * True when the modal should be shown — i.e. at least one candidate attribute
   * cleared the coverage threshold and is worth suggesting as a home.
   */
  shouldPrompt: boolean;
}

/**
 * Pluggable matching contract. The Option A implementation is fully
 * client-side; a future Bedrock-backed implementation can satisfy the same
 * interface (returning a Promise) without changing the UI.
 */
export interface AttributeMatcher {
  assess(
    formValues: CreateAttributeFormValues,
    existingAttributes: AttributeDetails[]
  ): AttributeMatchAssessment | Promise<AttributeMatchAssessment>;
}
